/**
 * Dashboard sub-nav. The flat tab strip across the top of the Dashboard
 * surface — **Announcements · Tasks** (the Jumbotron tab was removed by
 * the #740 wipe; the measurement board is rebuilt from the Contract).
 * Mirrors `client-success-nav.tsx`: each page declares its own `active`
 * prop rather than deriving it from the router — an explicit prop is
 * simpler than Link active-matching. `/` redirects to `/dashboard`, the
 * Tasks path. Announcements is the news only; Tasks is the operator
 * to-do queue.
 *
 * Unseen-news dot: the Announcements tab carries a small amber dot when
 * the feed changed since this browser last sat on the tab — otherwise an
 * operator landing on Tasks has no signal that news exists.
 * Amber is the standing "look/examine" cue (ADR-0134, the same hue the
 * Messages unread dot spends). Seen-state is a localStorage fingerprint
 * of the feed (ids + last-edit dates, so a post, edit, or delete
 * re-lights it) — per browser by design: "has *this operator here* read
 * the news" needs no server state, and the worst failure is the dot
 * showing once more.
 */

import { useEffect, useSyncExternalStore } from "react";
import { Link } from "@tanstack/react-router";
import type { Announcement } from "@command-center/shared";
import { cn } from "@/lib/utils";
import { useAnnouncements } from "@/lib/use-announcements";

export type DashboardTab = "announcements" | "tasks";

const TABS: { key: DashboardTab; label: string; path: string }[] = [
  { key: "announcements", label: "Announcements", path: "/dashboard/announcements" },
  { key: "tasks", label: "Tasks", path: "/dashboard" },
];

const ANNOUNCEMENTS_SEEN_KEY = "cc-announcements-seen";

/** Order-independent fingerprint of the feed; any post/edit/delete changes it. */
export function announcementsSignature(items: Announcement[]): string {
  return items
    .map((a) => `${a.id}:${a.updatedAt ?? a.postedAt}`)
    .sort()
    .join("|");
}

function readSeenSignature(): string | null {
  try {
    return localStorage.getItem(ANNOUNCEMENTS_SEEN_KEY);
  } catch {
    return null;
  }
}

/** Cross-tab wiring: the `storage` event fires in OTHER tabs when one tab
 *  stamps the seen key, so a dot showing in a second CC tab clears without
 *  waiting for an unrelated re-render. (Same-tab writes don't fire it, but
 *  the writing tab is sitting on Announcements, where the dot is suppressed,
 *  and the route change away re-renders anyway.) */
function subscribeToStorage(onChange: () => void): () => void {
  window.addEventListener("storage", onChange);
  return () => window.removeEventListener("storage", onChange);
}

export function DashboardNav({ active }: { active: DashboardTab }) {
  const announcements = useAnnouncements();
  const items = announcements.data?.items;
  const signature = items ? announcementsSignature(items) : null;
  const seenSignature = useSyncExternalStore(
    subscribeToStorage,
    readSeenSignature,
  );

  // Sitting on the Announcements tab IS seeing the news: stamp the current
  // fingerprint so the dot stays off after navigating away. (While here the
  // dot is suppressed below regardless.)
  useEffect(() => {
    if (active === "announcements" && signature !== null) {
      try {
        localStorage.setItem(ANNOUNCEMENTS_SEEN_KEY, signature);
      } catch {
        // Storage unavailable (private mode) — the dot just shows again.
      }
    }
  }, [active, signature]);

  // `signature` is null only while `items` is undefined, which the length
  // check already covers.
  const unseen =
    active !== "announcements" &&
    (items?.length ?? 0) > 0 &&
    seenSignature !== signature;

  return (
    <div
      className="mb-4 flex flex-wrap gap-1 border-b"
      role="tablist"
      data-testid="dashboard-nav"
    >
      {TABS.map((tab) => (
        <Link
          key={tab.key}
          to={tab.path}
          role="tab"
          aria-selected={tab.key === active}
          className={cn(
            "-mb-px border-b-2 px-3 py-2 text-sm transition-colors",
            tab.key === active
              ? "border-active-edge font-medium text-plane-foreground"
              : "border-transparent text-plane-muted hover:text-plane-foreground",
          )}
          data-testid={`dashboard-nav-${tab.key}`}
        >
          {tab.label}
          {tab.key === "announcements" && unseen ? (
            <span
              className="ml-1.5 inline-block h-1.5 w-1.5 rounded-full bg-warning align-middle"
              data-testid="announcements-unseen-dot"
            >
              <span className="sr-only">New announcements</span>
            </span>
          ) : null}
        </Link>
      ))}
    </div>
  );
}
